'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Logo } from '@/components/ui/Logo';
import { PHILIPPINES_COUNTRY } from '@/lib/recruitment/international';
import {
  INTL_DATA_STEPS,
  INTL_FILE_LABELS,
  INTL_STEPS,
  INTL_STEP_TITLES,
  IntlAudioPartKey,
  IntlFileKind,
  IntlStepKey,
  STEP_FIELDS,
} from '@/lib/recruitment/intl-application';
import {
  DataStep,
  IntlFormState,
  IntlFormValue,
  SaveStepResult,
} from '@/components/recruitment/intl/steps/DataStep';
import { IdentityStep } from '@/components/recruitment/intl/steps/IdentityStep';
import { DocumentsStep } from '@/components/recruitment/intl/steps/DocumentsStep';
import { AudioStep } from '@/components/recruitment/intl/steps/AudioStep';
import { TypingStep } from '@/components/recruitment/intl/steps/TypingStep';
import { ConsentStep } from '@/components/recruitment/intl/steps/ConsentStep';
import { FileUploadCard } from '@/components/recruitment/intl/FileUploadCard';
import { IntlConsentKey } from '@/lib/recruitment/intl-application';
import {
  DEFAULT_AGENT_DEFINITION,
  type AgentFormDefinition,
} from '@/lib/recruitment/job-application-definitions';

export type IntlBootstrapCandidate = {
  displayName: string;
  positionApplied: string | null;
} | null;

export type IntlBootstrapApplication = Record<string, unknown> & {
  completedSteps: string[];
  submitted: boolean;
  referenceNumber: string | null;
};

export type IntlBootstrapFile = { kind: string; fileName: string | null };
export type IntlBootstrapAudio = { part: string; durationSeconds: number | null };

/** Standalone public header shared by the careers pages (no links into the app).
 *  `subtitle` names the job of the application; defaults to the agent position. */
export function TeliCareersHeader({ subtitle }: { subtitle?: string } = {}) {
  return (
    <div className="bg-gray-900 border-b border-gray-800 px-4 sm:px-6 py-4 flex items-center gap-4">
      <Logo clickable={false} markOnly />
      <div className="h-5 w-px bg-gray-700" />
      <div className="min-w-0">
        <h1 className="text-base font-semibold text-white leading-tight">
          TELI International Recruitment
        </h1>
        <p className="text-xs text-gray-400 leading-tight">
          {subtitle ?? 'Remote Customer Service Representative · 100% Remote · Independent Contractor'}
        </p>
      </div>
    </div>
  );
}

function computeAge(dob: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return '';
  const d = new Date(`${dob}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const beforeBirthday =
    now.getMonth() < d.getMonth() ||
    (now.getMonth() === d.getMonth() && now.getDate() < d.getDate());
  if (beforeBirthday) age -= 1;
  return age > 0 && age <= 120 ? String(age) : '';
}

function initForm(application: IntlBootstrapApplication): IntlFormState {
  const form: IntlFormState = {};
  for (const step of INTL_DATA_STEPS) {
    for (const def of STEP_FIELDS[step]) {
      const raw = application[def.column];
      let value: IntlFormValue = null;
      if (raw !== null && raw !== undefined) {
        if (def.type === 'bool') value = raw === true;
        else if (def.type === 'number' || def.type === 'duration') value = String(raw);
        else if (def.type === 'string_array') value = Array.isArray(raw) ? (raw as string[]) : [];
        else value = typeof raw === 'string' ? raw : String(raw);
      }
      form[def.column] = value;
    }
  }
  if (!form.country) form.country = PHILIPPINES_COUNTRY;
  return form;
}

function firstPendingIndex(completedSteps: string[]): number {
  const idx = INTL_STEPS.findIndex((s) => !completedSteps.includes(s));
  return idx === -1 ? INTL_STEPS.length - 1 : idx;
}

export function IntlApplicationWizard({
  token,
  candidate,
  application,
  files: initialFiles,
  audio: initialAudio,
  definition = DEFAULT_AGENT_DEFINITION,
  onExpired,
}: {
  token: string;
  candidate: IntlBootstrapCandidate;
  application: IntlBootstrapApplication;
  files: IntlBootstrapFile[];
  audio: IntlBootstrapAudio[];
  /** The form as worded in Recruitment -> Job Applications (code defaults otherwise). */
  definition?: AgentFormDefinition;
  onExpired: () => void;
}) {
  const stepTitle = (s: IntlStepKey) => definition.stepTitles[s] ?? INTL_STEP_TITLES[s];
  const headerSubtitle = `${definition.info.jobTitle} · ${definition.info.tagline}`;
  const [form, setForm] = useState<IntlFormState>(() => initForm(application));
  const [completedSteps, setCompletedSteps] = useState<string[]>(application.completedSteps);
  const [files, setFiles] = useState<Partial<Record<IntlFileKind, string | null>>>(() => {
    const out: Partial<Record<IntlFileKind, string | null>> = {};
    for (const f of initialFiles) out[f.kind as IntlFileKind] = f.fileName ?? 'Uploaded';
    return out;
  });
  // Ref mirrors so the stable saveDataStep callback sees the current uploads and answers.
  const filesRef = useRef(files);
  filesRef.current = files;
  const formRef = useRef(form);
  formRef.current = form;
  const [audio, setAudio] = useState<Partial<Record<IntlAudioPartKey, number | null>>>(() => {
    const out: Partial<Record<IntlAudioPartKey, number | null>> = {};
    for (const a of initialAudio) out[a.part as IntlAudioPartKey] = a.durationSeconds ?? 0;
    return out;
  });
  const [submitted, setSubmitted] = useState(application.submitted);
  const [referenceNumber, setReferenceNumber] = useState<string | null>(application.referenceNumber);
  const [stepIndex, setStepIndex] = useState(() => firstPendingIndex(application.completedSteps));

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [stepIndex]);

  const step = INTL_STEPS[stepIndex];

  // Completed steps stay navigable for review/edit; locked ones open as the
  // candidate progresses (frontier = first step not yet completed).
  const frontier = useMemo(() => firstPendingIndex(completedSteps), [completedSteps]);
  const canOpen = useCallback(
    (index: number) => index <= frontier || completedSteps.includes(INTL_STEPS[index]),
    [frontier, completedSteps]
  );

  const goTo = useCallback(
    (index: number) => {
      if (index >= 0 && index < INTL_STEPS.length && canOpen(index)) setStepIndex(index);
    },
    [canOpen]
  );
  const advance = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, INTL_STEPS.length - 1));
  }, []);
  const goBack = stepIndex > 0 ? () => setStepIndex((i) => Math.max(i - 1, 0)) : null;

  const setField = useCallback((column: string, value: IntlFormValue) => {
    setForm((f) => {
      const next = { ...f, [column]: value };
      if (column === 'date_of_birth' && typeof value === 'string') {
        next.age = computeAge(value);
      }
      return next;
    });
  }, []);

  const saveDataStep = useCallback(
    (dataStep: IntlStepKey) =>
      async (payload: Record<string, unknown>): Promise<SaveStepResult> => {
        // The screenshots live inside the Equipment step but are hard submit
        // prerequisites - gate Continue here so nobody reaches the final
        // submit only to be bounced back to this step. The backup device
        // screenshot is only asked when both computers are used.
        if (dataStep === 'equipment') {
          const uploads = filesRef.current;
          const required: IntlFileKind[] = ['specs', 'speedtest'];
          if (formRef.current.computer_type === 'Both') required.splice(1, 0, 'specs_backup');
          const missingUploads = required
            .filter((kind) => !uploads[kind])
            .map((kind) => `${definition.files[kind]?.label ?? INTL_FILE_LABELS[kind]} (upload it above)`);
          if (missingUploads.length > 0) return { ok: false, missing: missingUploads };
        }
        try {
          const res = await fetch(`/api/public/intl-recruitment/${token}/steps/${dataStep}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (res.status === 410) {
            onExpired();
            return { ok: false };
          }
          const d = await res.json().catch(() => ({}));
          if (!res.ok) {
            return {
              ok: false,
              missing: Array.isArray(d.missing) ? d.missing : undefined,
              error: d.error || 'Could not save this step. Please try again.',
            };
          }
          if (Array.isArray(d.completedSteps)) setCompletedSteps(d.completedSteps);
          advance();
          return { ok: true };
        } catch {
          return { ok: false, error: 'Could not save. Check your connection and try again.' };
        }
      },
    [token, onExpired, advance, definition]
  );

  const handleFileUploaded = useCallback(
    (kind: IntlFileKind, fileName: string, newCompletedSteps: string[]) => {
      setFiles((f) => ({ ...f, [kind]: fileName }));
      if (newCompletedSteps.length > 0) setCompletedSteps(newCompletedSteps);
    },
    []
  );

  const handleAudioUploaded = useCallback(
    (part: IntlAudioPartKey, durationSeconds: number, newCompletedSteps: string[]) => {
      setAudio((a) => ({ ...a, [part]: durationSeconds }));
      if (newCompletedSteps.length > 0) setCompletedSteps(newCompletedSteps);
    },
    []
  );

  const submitApplication = useCallback(
    async (consents: Record<IntlConsentKey, boolean>, signatureFullName: string) => {
      try {
        const res = await fetch(`/api/public/intl-recruitment/${token}/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            consentDataProcessing: consents.consent_data_processing,
            consentRecordedInterviews: consents.consent_recorded_interviews,
            consentAccuracy: consents.consent_accuracy,
            signatureFullName,
          }),
        });
        if (res.status === 410) {
          onExpired();
          return { ok: false };
        }
        const d = await res.json().catch(() => ({}));
        if (!res.ok) {
          return {
            ok: false,
            missing: Array.isArray(d.missing) ? d.missing : undefined,
            error: d.error || 'Could not submit the application. Please try again.',
          };
        }
        setReferenceNumber(typeof d.referenceNumber === 'string' ? d.referenceNumber : null);
        setSubmitted(true);
        window.scrollTo({ top: 0 });
        return { ok: true };
      } catch {
        return { ok: false, error: 'Could not submit. Check your connection and try again.' };
      }
    },
    [token, onExpired]
  );

  // ── Submitted - read-only confirmation ────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <TeliCareersHeader subtitle={headerSubtitle} />
        <div className="max-w-2xl mx-auto px-4 py-16 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Application Submitted!</h1>
            <p className="text-gray-400 text-sm mb-6">
              Thank you{candidate ? <>, <strong className="text-white">{candidate.displayName}</strong></> : ''}.
              We have received your application. Our recruitment team will review it and contact
              you by email or WhatsApp.
            </p>
            {referenceNumber && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-4">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">
                  Your reference number
                </p>
                <p className="text-3xl font-bold font-mono text-emerald-400">{referenceNumber}</p>
              </div>
            )}
            <p className="text-xs text-gray-500">
              Please save this reference number - you will need it for any follow-up about your
              application.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Wizard ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <TeliCareersHeader subtitle={headerSubtitle} />

      <div className="max-w-2xl mx-auto px-4 py-6 pb-16">
        {candidate && (
          <p className="text-xs text-gray-500 mb-4">
            Applicant: <span className="text-gray-300">{candidate.displayName}</span>
          </p>
        )}

        {/* Progress */}
        <div className="mb-3">
          <div className="flex items-end justify-between mb-2">
            <p className="text-sm font-semibold text-white">{stepTitle(step)}</p>
            <p className="text-xs text-gray-400">
              Step {stepIndex + 1} of {INTL_STEPS.length}
            </p>
          </div>
          <div className="h-2 rounded-full bg-gray-800 overflow-hidden">
            <div
              className="h-full bg-emerald-600 transition-all duration-300"
              style={{ width: `${((stepIndex + 1) / INTL_STEPS.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Step chips - completed steps stay open for review */}
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-4 -mx-4 px-4">
          {INTL_STEPS.map((s, i) => {
            const done = completedSteps.includes(s);
            const active = i === stepIndex;
            const open = canOpen(i);
            return (
              <button
                key={s}
                type="button"
                onClick={() => goTo(i)}
                disabled={!open}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : done
                      ? 'bg-emerald-900/30 border-emerald-800 text-emerald-300'
                      : open
                        ? 'bg-gray-900 border-gray-700 text-gray-300'
                        : 'bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed'
                }`}
              >
                {done && !active ? '✓ ' : `${i + 1}. `}
                {stepTitle(s)}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-gray-600 mb-5">
          Your progress is saved after each step - keep this page&apos;s link to resume your
          application later.
        </p>

        {INTL_DATA_STEPS.includes(step) ? (
          <DataStep
            key={step}
            step={step}
            defs={definition.stepFields[step] ?? STEP_FIELDS[step]}
            form={form}
            setField={setField}
            onSave={saveDataStep(step)}
            onBack={goBack}
            slotsAfter={
              step === 'equipment'
                ? {
                    // The screenshots sit under the answers they document
                    // (DataStep only renders a slot while its question is shown).
                    free_storage_gb: (
                      <FileUploadCard
                        token={token}
                        kind="specs"
                        accept="image_pdf"
                        label={definition.files.specs?.label}
                        hint={definition.files.specs?.hint || 'A screenshot of the computer specifications: OS, processor, and RAM. JPG, PNG, or PDF.'}
                        fileName={files.specs ?? null}
                        onUploaded={handleFileUploaded}
                        onExpired={onExpired}
                      />
                    ),
                    backup_free_storage_gb: (
                      <FileUploadCard
                        token={token}
                        kind="specs_backup"
                        accept="image_pdf"
                        label={definition.files.specs_backup?.label}
                        hint={definition.files.specs_backup?.hint || 'A screenshot of the backup device specifications: OS, processor, and RAM. JPG, PNG, or PDF.'}
                        fileName={files.specs_backup ?? null}
                        onUploaded={handleFileUploaded}
                        onExpired={onExpired}
                      />
                    ),
                    connection_type: (
                      <FileUploadCard
                        token={token}
                        kind="speedtest"
                        label={definition.files.speedtest?.label}
                        hint={definition.files.speedtest?.hint || 'Run a test at speedtest.net and upload a screenshot of the result.'}
                        fileName={files.speedtest ?? null}
                        onUploaded={handleFileUploaded}
                        onExpired={onExpired}
                      />
                    ),
                  }
                : undefined
            }
          />
        ) : step === 'identity' ? (
          <IdentityStep
            token={token}
            labels={definition.files}
            files={files}
            onUploaded={handleFileUploaded}
            onExpired={onExpired}
            onBack={goBack}
            onContinue={advance}
          />
        ) : step === 'audio' ? (
          <AudioStep
            token={token}
            parts={definition.audioParts}
            instructions={definition.audioInstructions}
            audio={audio}
            onUploaded={handleAudioUploaded}
            onExpired={onExpired}
            onBack={goBack}
            onContinue={advance}
          />
        ) : step === 'documents' ? (
          <DocumentsStep
            token={token}
            labels={definition.files}
            files={files}
            onUploaded={handleFileUploaded}
            onExpired={onExpired}
            onBack={goBack}
            onContinue={advance}
          />
        ) : step === 'typing' ? (
          <TypingStep
            token={token}
            initialResult={
              typeof application.typing_wpm === 'number' &&
              typeof application.typing_accuracy === 'number'
                ? {
                    wpm: application.typing_wpm,
                    accuracy: application.typing_accuracy,
                    attempts:
                      typeof application.typing_attempts === 'number'
                        ? application.typing_attempts
                        : 1,
                  }
                : null
            }
            initialStoryKey={
              typeof application.typing_story_key === 'string'
                ? application.typing_story_key
                : null
            }
            onCompleted={setCompletedSteps}
            onExpired={onExpired}
            onBack={goBack}
            onContinue={advance}
          />
        ) : (
          <ConsentStep items={definition.consentItems} onSubmit={submitApplication} onBack={goBack} />
        )}
      </div>
    </div>
  );
}
